import { computed, inject, Injectable, signal } from '@angular/core';
import { Brand, KeyMessage } from '@omni/classes/brand/brand.class';
import { IoConfigurationFeatureCodes } from '@omni/classes/authentication/user.class';
import { AuthenticationService } from '@omni/services/authentication.service';
import { BrandOfflineService } from '@omni/services/brand/brand.service';
import { ActivityService } from '@omni/services/activity/activity.service';
import { AppointmentActivity } from '@omni/classes/activity/appointment.activity.class';
import { MeetingActivityState } from '@omni/classes/activity/activity.class';
import { AgendaFooterService } from '@omni/services/footer/agenda-footer.service';
import { cloneDeep, isEqual, uniqBy } from 'lodash-es';
import {
  PkmDefaultProductConfig, PkmDeltaPayload, PkmAddedProduct,
  PkmModifiedProduct, PkmRemovedProduct, PkmReorderProduct,
  PkmKeyMessagePayloadItem, PkmRemovedKeyMessage,
} from '@omni/components/activity/product-key-messages-v2/pkm.types';

/**
 * Component-scoped signal-based state management for PKM V2.
 * Provided at the component level via `providers: [PkmStateService]`.
 */
@Injectable()
export class PkmStateService {
  private readonly brandService = inject(BrandOfflineService);
  private readonly authService = inject(AuthenticationService);
  private readonly activityService = inject(ActivityService);
  private readonly agendaFooterService = inject(AgendaFooterService);

  // --- Source signals ---
  readonly selectedProducts = signal<Brand[]>([]);
  readonly searchQuery = signal('');

  // --- Read-only context signals ---
  readonly isReadOnlyJointMeeting = signal(false);
  readonly backgroundUploadInProgress = signal(false);
  readonly activityCompleted = signal(false);
  readonly activityCanceled = signal(false);
  readonly isDiffPosition = signal(false);

  // Snapshot for delta computation
  private savedSnapshot = signal<Brand[]>([]);

  // Config cache
  private _defaultProductsConfig: PkmDefaultProductConfig[] | null = null;

  // --- Computed signals ---

  /**
   * Compact mode: all user's available products are present in the configured preferred list.
   * Config may have more products than available — that's fine.
   * When true, no search bar is shown — all products appear inline as a checklist.
   */
  readonly isCompactMode = computed(() => {
    const configs = this.getDefaultProductsConfig();
    if (!configs?.length) return false;

    const available = this.filteredBrands();
    if (!available.length) return false;

    const configIds = new Set(configs.map(c => c.id.toLowerCase()));

    // Every available product must be in config (config can have extra)
    return available.every(b => configIds.has(b.ID?.toLowerCase()));
  });

  readonly displayProducts = computed(() => {
    const products = this.selectedProducts();
    if (this.activityCompleted() || this.activityCanceled()) {
      return products.filter(p => p.isSelected);
    }
    return products.filter(p => p.isSelected || p.isAutoSelected);
  });

  /**
   * Compact mode display: merges selectedProducts with all filteredBrands
   * so every available product is always shown.
   * Selected products on top (by priority), unselected below (by config order).
   */
  readonly compactDisplayProducts = computed(() => {
    const configs = this.getDefaultProductsConfig();
    const configMap = new Map((configs || []).map(c => [c.id.toLowerCase(), c.order]));
    const savedProducts = this.selectedProducts();
    const savedMap = new Map(savedProducts.map(p => [p.ID, p]));

    // Build full list: use saved state if available, otherwise raw brand
    const allBrands = this.filteredBrands();
    const merged = allBrands.map(brand => {
      const saved = savedMap.get(brand.ID);
      return saved || brand;
    });

    const selected = merged
      .filter(p => p.isSelected || p.isAutoSelected)
      .sort((a, b) => (a.priority || 0) - (b.priority || 0));

    if (this.activityCompleted() || this.activityCanceled()) {
      return selected;
    }

    const selectedIds = new Set(selected.map(p => p.ID));
    const unselected = merged
      .filter(p => !selectedIds.has(p.ID))
      .sort((a, b) => {
        const aOrder = configMap.get(a.ID?.toLowerCase()) ?? 999;
        const bOrder = configMap.get(b.ID?.toLowerCase()) ?? 999;
        if (aOrder !== bOrder) return aOrder - bOrder;
        return (a.name || '').localeCompare(b.name || '');
      });

    return [...selected, ...unselected];
  });

  readonly availableForSearch = computed(() => {
    const query = this.searchQuery().toLowerCase().trim();
    const queryWords = query ? query.split(' ').filter(w => w) : [];

    const configs = this.getDefaultProductsConfig();
    const configMap = new Map((configs || []).map(c => [c.id.toLowerCase(), c.order]));
    const savedProducts = this.selectedProducts();
    const savedMap = new Map(savedProducts.map(p => [p.ID, p]));

    // Build full list: use saved state if available, otherwise raw brand
    const allBrands = this.filteredBrands();
    const merged = allBrands.map(brand => savedMap.get(brand.ID) || brand);

    // 1. Selected products in user's reordered sequence
    const selected = merged
      .filter(p => p.isSelected || p.isAutoSelected)
      .sort((a, b) => (a.priority || 0) - (b.priority || 0));
    const selectedIds = new Set(selected.map(p => p.ID));

    const notSelected = merged.filter(p => !selectedIds.has(p.ID));

    // 2. Default/preferred products (not selected), sorted by config order
    const defaultProducts = notSelected
      .filter(b => configMap.has(b.ID?.toLowerCase()))
      .sort((a, b) => (configMap.get(a.ID.toLowerCase()) ?? 0) - (configMap.get(b.ID.toLowerCase()) ?? 0));
    const defaultIds = new Set(defaultProducts.map(p => p.ID));

    // 3. Remaining products alphabetically
    const rest = notSelected
      .filter(b => !defaultIds.has(b.ID))
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

    const all = [...selected, ...defaultProducts, ...rest];

    // Apply search filter
    return all.filter(b => {
      if (!queryWords.length) return true;
      const name = (b.name || '').toLowerCase();
      const productNumber = b.productnumber ? b.productnumber.toString().toLowerCase() : '';
      const kmNames = b.keyMessages ? b.keyMessages.map(km => (km.name || '').toLowerCase()) : [];
      return queryWords.every(word =>
        name.includes(word) || productNumber.includes(word) || kmNames.some(km => km.includes(word))
      );
    });
  });

  /** All preferred/default products from config — selected first, then unselected in config order */
  readonly preferredProducts = computed(() => {
    const configs = this.getDefaultProductsConfig();
    if (!configs?.length) return [];

    const configMap = new Map(configs.map(c => [c.id.toLowerCase(), c.order]));
    const configIds = new Set(configs.map(c => c.id.toLowerCase()));

    // 1. Selected config products in user's reorder sequence
    const addedInConfig = this.selectedProducts()
      .filter(p => (p.isSelected || p.isAutoSelected) && configIds.has(p.ID?.toLowerCase()));
    const addedIds = new Set(addedInConfig.map(p => p.ID));

    // 2. Remaining config products not selected, in config order
    const remainingConfig = this.filteredBrands()
      .filter(b => configIds.has(b.ID?.toLowerCase()) && !addedIds.has(b.ID))
      .sort((a, b) => (configMap.get(a.ID.toLowerCase()) ?? 0) - (configMap.get(b.ID.toLowerCase()) ?? 0));

    return [...addedInConfig, ...remainingConfig];
  });

  readonly hasUnsavedChanges = computed(() => {
    const current = this.selectedProducts()
      .filter(p => p.isSelected || p.isAutoSelected)
      .map(p => ({
        id: p.ID,
        priority: p.priority,
        keyMessages: p.keyMessages?.filter(km => km.isSelected || km.isAutoSelected).map(km => km.ID) || []
      }));
    const snapshot = this.savedSnapshot()
      .filter(p => p.isSelected || p.isAutoSelected)
      .map(p => ({
        id: p.ID,
        priority: p.priority,
        keyMessages: p.keyMessages?.filter(km => km.isSelected || km.isAutoSelected).map(km => km.ID) || []
      }));
    return !isEqual(current, snapshot);
  });

  readonly isReadOnly = computed(() => {
    return this.activityCompleted()
      || this.activityCanceled()
      || this.isReadOnlyJointMeeting()
      || this.enableReadonlyViewForMeetings()
      || this.backgroundUploadInProgress()
      || this.isDiffPosition();
  });

  private readonly enableReadonlyViewForMeetings = computed(() => {
    const isTeamView = this.activityService.teamViewActive;
    if (
      this.activityService.isMyUserSelectedInTeamView &&
      this.agendaFooterService.actSegment === 'week' &&
      this.activityService.selectedActivity &&
      this.activityService.selectedActivity instanceof AppointmentActivity &&
      this.activityService.activities.some(a => a.ID === this.activityService.selectedActivity.ID)
    ) {
      return false;
    }
    return isTeamView;
  });

  // --- Methods ---

  initializeForActivity(activity: AppointmentActivity): void {
    this.searchQuery.set('');

    this.activityCompleted.set(activity?.isCompleted || false);
    this.activityCanceled.set(activity?.state === MeetingActivityState.Canceled);
    this.isDiffPosition.set(activity?.isDiffPosition || false);

    let products: Brand[] = [];

    if (activity?.products?.length) {
      products = cloneDeep(activity.products);
      const hasUserSelections = products.some(p => p.isSelected && !p.isAutoSelected);

      if (!hasUserSelections) {
        products = this.applyDefaultProducts(products);
      }
    } else {
      // No products yet (new meeting) — start with empty list
      // Preferred/default products are shown only in the search popover, not auto-added
      products = [];
    }

    // Handle content/presentation auto-selected brands
    if ((activity as AppointmentActivity)?.presentations?.length) {
      products = this.mergeContentBrands(products, activity);
    }

    products = uniqBy(products, 'ID');
    this.selectedProducts.set(products);
    this.savedSnapshot.set(cloneDeep(products));
  }

  updateActivityStateSignals(activity: AppointmentActivity): void {
    this.activityCompleted.set(activity?.isCompleted || false);
    this.activityCanceled.set(activity?.state === MeetingActivityState.Canceled);
    this.isDiffPosition.set(activity?.isDiffPosition || false);
  }

  setProducts(brands: Brand[]): void {
    const products = cloneDeep(brands);
    const selected = products.filter(p => p.isSelected || p.isAutoSelected);

    if (selected.length) {
      const unique = uniqBy(selected, 'ID');
      this.recalculatePriorities(unique);
      this.selectedProducts.set(unique);
    } else {
      this.selectedProducts.set([]);
    }
    this.savedSnapshot.set(cloneDeep(this.selectedProducts()));
  }

  toggleKeyMessage(product: Brand, keyMessage: KeyMessage): void {
    if (this.isReadOnly() || keyMessage.isAutoSelected) return;

    const products = cloneDeep(this.selectedProducts());
    const targetProduct = products.find(p => p.ID === product.ID);
    if (!targetProduct) return;

    const targetKm = targetProduct.keyMessages?.find(km => km.ID === keyMessage.ID);
    if (!targetKm) return;

    targetKm.isSelected = !targetKm.isSelected;

    // Auto-select product when a key message is selected
    if (targetKm.isSelected && !targetProduct.isSelected && !targetProduct.isAutoSelected) {
      targetProduct.isSelected = true;
    }

    this.selectedProducts.set(products);
  }

  removeProduct(product: Brand): void {
    if (this.isReadOnly() || product.isAutoSelected) return;

    const products = cloneDeep(this.selectedProducts());
    const target = products.find(p => p.ID === product.ID);
    if (!target) return;

    target.isSelected = false;
    target.keyMessages?.forEach(km => { km.isSelected = false; });

    this.recalculatePriorities(products);
    this.selectedProducts.set(products);
  }

  reorder(fromIndex: number, toIndex: number): void {
    const products = [...this.selectedProducts()];
    const display = this.displayProducts();

    // Map display indices to selectedProducts indices
    const fromProduct = display[fromIndex];
    const toProduct = display[toIndex];
    if (!fromProduct || !toProduct) return;

    const actualFrom = products.findIndex(p => p.ID === fromProduct.ID);
    const actualTo = products.findIndex(p => p.ID === toProduct.ID);
    if (actualFrom === -1 || actualTo === -1) return;

    const [element] = products.splice(actualFrom, 1);
    products.splice(actualTo, 0, element);

    this.recalculatePriorities(products);
    this.selectedProducts.set(products);
  }

  // --- Compact mode methods ---

  /**
   * Toggle product in compact mode. On select, moves product to the end of
   * selected products (top section). On deselect, clears KMs and moves to bottom.
   */
  compactToggleProduct(product: Brand): void {
    if (this.isReadOnly() || product.isAutoSelected) return;

    // Block removal if product has auto-selected KMs
    const hasAutoKm = product.keyMessages?.some(km => km.isAutoSelected) || false;
    if (hasAutoKm && (product.isSelected || product.isAutoSelected)) return;

    const products = cloneDeep(this.selectedProducts());
    let target = products.find(p => p.ID === product.ID);

    // Product may not be in selectedProducts yet (shown via compactDisplayProducts merge)
    if (!target) {
      target = cloneDeep(product);
      target.isSelected = true;
      products.push(target);
    } else {
      const wasSelected = target.isSelected;
      target.isSelected = !target.isSelected;
      if (!target.isSelected) {
        target.keyMessages?.forEach(km => { km.isSelected = false; });
      } else if (!wasSelected) {
        // Move to end of array so it appears at bottom of selected section
        const idx = products.indexOf(target);
        products.splice(idx, 1);
        products.push(target);
      }
    }

    this.recalculatePriorities(products);
    this.selectedProducts.set(products);
  }

  /**
   * Toggle KM in compact mode. Auto-selects the product if not already selected.
   * Moves newly-selected product to the end of the selected section.
   */
  compactToggleKeyMessage(product: Brand, keyMessage: KeyMessage): void {
    if (this.isReadOnly() || keyMessage.isAutoSelected) return;

    const products = cloneDeep(this.selectedProducts());
    let targetProduct = products.find(p => p.ID === product.ID);

    // Product may not be in selectedProducts yet
    if (!targetProduct) {
      targetProduct = cloneDeep(product);
      targetProduct.keyMessages?.forEach(km => { km.isSelected = false; });
      products.push(targetProduct);
    }

    const targetKm = targetProduct.keyMessages?.find(km => km.ID === keyMessage.ID);
    if (!targetKm) return;

    targetKm.isSelected = !targetKm.isSelected;

    // Auto-select product when a KM is selected — move to end of selected section
    const wasSelected = targetProduct.isSelected || targetProduct.isAutoSelected;
    if (targetKm.isSelected && !wasSelected) {
      targetProduct.isSelected = true;
      const idx = products.indexOf(targetProduct);
      products.splice(idx, 1);
      products.push(targetProduct);
    }

    this.recalculatePriorities(products);
    this.selectedProducts.set(products);
  }

  /**
   * Reorder in compact mode — operates on the compactDisplayProducts order.
   */
  compactReorder(fromIndex: number, toIndex: number): void {
    const compact = this.compactDisplayProducts();
    const fromProduct = compact[fromIndex];
    const toProduct = compact[toIndex];
    if (!fromProduct || !toProduct) return;

    // Guard: only allow reorder within selected products
    const isFromSelected = fromProduct.isSelected || fromProduct.isAutoSelected;
    const isToSelected = toProduct.isSelected || toProduct.isAutoSelected;
    if (!isFromSelected || !isToSelected) return;

    const products = [...this.selectedProducts()];
    const actualFrom = products.findIndex(p => p.ID === fromProduct.ID);
    const actualTo = products.findIndex(p => p.ID === toProduct.ID);
    if (actualFrom === -1 || actualTo === -1) return;

    const [element] = products.splice(actualFrom, 1);
    products.splice(actualTo, 0, element);

    this.recalculatePriorities(products);
    this.selectedProducts.set(products);
  }


  /** Add a product by barcode scan — appends to end of selected list */
  addScannedProduct(product: Brand): void {
    const products = cloneDeep(this.selectedProducts());
    const cloned = cloneDeep(product);
    cloned.isSelected = true;
    products.push(cloned);
    this.recalculatePriorities(products);
    this.selectedProducts.set(products);
  }

  getSavedSnapshot(): Brand[] {
    return this.savedSnapshot();
  }

  /** Revert snapshot to a specific state (used on save failure) */
  revertSnapshotTo(snapshot: Brand[]): void {
    this.savedSnapshot.set(cloneDeep(snapshot));
  }

  /** Recalculate priorities and sort key messages alphabetically */
  private recalculatePriorities(products: Brand[]): void {
    let priority = 1;
    products.forEach(p => {
      if (p.isSelected || p.isAutoSelected) {
        p.priority = priority++;
      }
      if (p.keyMessages?.length > 1) {
        p.keyMessages.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      }
    });
  }

  computeDelta(): PkmDeltaPayload {
    const current = this.selectedProducts().filter(p => p.isSelected || p.isAutoSelected);
    const saved = this.savedSnapshot().filter(p => p.isSelected || p.isAutoSelected);
    const savedMap = new Map(saved.map(p => [p.ID, p]));
    const currentMap = new Map(current.map(p => [p.ID, p]));

    const delta: PkmDeltaPayload = {};

    // Added products — sequence based on position in the full current list
    const added: PkmAddedProduct[] = current
      .filter(p => !savedMap.has(p.ID))
      .map(p => ({
        indskr_productid: p.ID,
        productName: p.name,
        indskr_sequence: p.priority || (current.indexOf(p) + 1),
        indskr_isautomaticallyselected: p.isAutoSelected || false,
        activityProductKeyMessages: (p.keyMessages || [])
          .filter(km => km.isSelected || km.isAutoSelected)
          .map(km => ({ indskr_keymessageid: km.ID, indskr_name: km.name, indskr_isautomaticallyselected: km.isAutoSelected || false })),
      }));
    if (added.length) delta.added = added;

    // Removed products
    const removed: PkmRemovedProduct[] = saved
      .filter(p => !currentMap.has(p.ID))
      .map(p => ({ indskr_productid: p.ID }));
    if (removed.length) delta.removed = removed;

    // Modified products — compute KM-level added/removed
    const modified: PkmModifiedProduct[] = [];
    current.forEach(p => {
      const savedProduct = savedMap.get(p.ID);
      if (!savedProduct) return; // new product, handled above

      const currentKmIds = new Set((p.keyMessages || []).filter(km => km.isSelected || km.isAutoSelected).map(km => km.ID));
      const savedKmIds = new Set((savedProduct.keyMessages || []).filter(km => km.isSelected || km.isAutoSelected).map(km => km.ID));

      const addedKms: PkmKeyMessagePayloadItem[] = (p.keyMessages || [])
        .filter(km => (km.isSelected || km.isAutoSelected) && !savedKmIds.has(km.ID))
        .map(km => ({ indskr_keymessageid: km.ID, indskr_name: km.name, indskr_isautomaticallyselected: km.isAutoSelected || false }));

      const removedKms: PkmRemovedKeyMessage[] = (savedProduct.keyMessages || [])
        .filter(km => (km.isSelected || km.isAutoSelected) && !currentKmIds.has(km.ID))
        .map(km => ({ indskr_keymessageid: km.ID }));

      const hasKmChanges = addedKms.length > 0 || removedKms.length > 0;

      // Only include in modified if there are KM changes — sequence changes go in reorder
      if (hasKmChanges) {
        const mod: PkmModifiedProduct = { indskr_productid: p.ID };
        if (addedKms.length) mod.addedKeyMessages = addedKms;
        if (removedKms.length) mod.removedKeyMessages = removedKms;
        modified.push(mod);
      }
    });
    if (modified.length) delta.modified = modified;

    // Reorder — detect sequence changes across all products
    const reorder: PkmReorderProduct[] = [];
    current.forEach((p, idx) => {
      const savedProduct = savedMap.get(p.ID);
      if (savedProduct && p.priority !== savedProduct.priority) {
        reorder.push({ indskr_productid: p.ID, indskr_sequence: p.priority || (idx + 1) });
      }
    });
    if (reorder.length) delta.reorder = reorder;

    return delta;
  }

  updateKeyMessagesSelection(brands: Brand[]): void {
    if (!brands?.length) return;

    const products = cloneDeep(this.selectedProducts());
    brands.forEach(brand => {
      const target = products.find(p => p.ID === brand.ID);
      if (!target) return;
      target.priority = brand.priority;
      brand.keyMessages?.forEach(km => {
        if (km.isSelected) {
          const targetKm = target.keyMessages?.find(k => k.ID === km.ID);
          if (targetKm) targetKm.isSelected = true;
        }
      });
    });
    this.selectedProducts.set(products);
  }

  addContentBrands(addedContent: any[]): void {
    if (!addedContent?.length) return;

    const products = cloneDeep(this.selectedProducts());
    const allBrands = cloneDeep(this.filteredBrands());
    const activityProducts = (this.activityService.selectedActivity as AppointmentActivity)?.activityProducts;
    const sourceBrands = activityProducts?.length ? cloneDeep(activityProducts) : allBrands;

    const contentBrands = this.extractContentBrands(addedContent, sourceBrands);

    contentBrands.forEach(cb => {
      const existing = products.find(p => p.ID === cb.ID);
      if (existing) {
        existing.isSelected = true;
        existing.isAutoSelected = true;
      } else {
        products.push(cb);
      }
    });

    this.selectedProducts.set(uniqBy(products, 'ID'));
  }

  removeContentBrands(removedContents: any[]): void {
    if (!removedContents?.length) return;

    const products = cloneDeep(this.selectedProducts());
    let changed = false;

    removedContents.forEach(removedContent => {
      if (!removedContent?.brands || !Array.isArray(removedContent.brands)) return;
      removedContent.brands.forEach((brand: any) => {
        const idx = products.findIndex(p =>
          (p.name === brand || p.name === brand?.name || p.ID === brand?.ID) &&
          p.isSelected && p.isAutoSelected
        );
        if (idx > -1) {
          products.splice(idx, 1);
          changed = true;
        }
      });
    });

    if (changed) {
      this.selectedProducts.set(products);
    }
  }

  addCovisitorProducts(covisitorProducts: any[]): void {
    if (!covisitorProducts?.length) return;

    const products = cloneDeep(this.selectedProducts());
    const filtered = covisitorProducts.filter(pr => pr['productStructure'] !== 3);

    filtered.forEach(pro => {
      const brand = new Brand(pro, pro.indskr_sequence, true);
      const existing = products.find(p => p.ID === brand.ID);
      if (!existing) {
        products.push(brand);
      }
    });

    this.selectedProducts.set(uniqBy(products, 'ID'));
  }

  // --- Private helpers ---

  private applyDefaultProducts(brands: Brand[]): Brand[] {
    const defaultConfigs = this.getDefaultProductsConfig();
    if (!defaultConfigs?.length) return brands;

    const hasUserSelections = brands.some(p => p.isSelected && !p.isAutoSelected);
    if (hasUserSelections) return brands;

    const configMap = new Map(defaultConfigs.map(c => [c.id.toLowerCase(), c.order]));

    brands.forEach(brand => {
      const order = configMap.get(brand.ID?.toLowerCase());
      if (order !== undefined) {
        brand.isSelected = true;
        brand.priority = order;
      }
    });

    return brands;
  }

  private getDefaultProductsConfig(): PkmDefaultProductConfig[] {
    if (this._defaultProductsConfig) return this._defaultProductsConfig;

    try {
      const configValue = this.authService.hasIoConfigWithFeatureCode(
        IoConfigurationFeatureCodes.BU_ACTIVITY_GA_DEFAULT_PKM_PRODUCTS
      );
      if (configValue && typeof configValue === 'string') {
        const parsed = JSON.parse(configValue);
        if (Array.isArray(parsed)) {
          this._defaultProductsConfig = parsed.filter((e: any) => e?.id);
          return this._defaultProductsConfig;
        }
      }
    } catch (e) {
      console.warn('Failed to parse BU_ACTIVITY_GA_DEFAULT_PKM_PRODUCTS config', e);
    }

    this._defaultProductsConfig = [];
    return this._defaultProductsConfig;
  }

  private mergeContentBrands(products: Brand[], activity: AppointmentActivity): Brand[] {
    const presentations = activity?.presentations;
    if (!presentations?.length) return products;

    const allBrands = this.filteredBrands();

    presentations.forEach((content: any) => {
      if (!content?.brands?.length) return;
      content.brands.forEach((brandRef: any) => {
        const brandName = brandRef?.name || brandRef;
        const match = allBrands.find(b => b.name === brandName);
        if (!match) return;

        // Mark the saved product in place — its key messages carry what was
        // actually presented. allBrands is the shared master list, so never
        // mutate a match; clone it when the brand is new to the meeting.
        const existing = products.find(p => p.ID === match.ID);
        if (existing) {
          existing.isSelected = true;
          existing.isAutoSelected = true;
          return;
        }

        const added = cloneDeep(match);
        added.isSelected = true;
        added.isAutoSelected = true;
        products.push(added);
      });
    });

    return products;
  }

  private extractContentBrands(addedContent: any[], sourceBrands: Brand[]): Brand[] {
    const contentBrands: Brand[] = [];
    const brandNames: string[] = [];

    addedContent.forEach((content: any) => {
      if (!content?.brands?.length) return;
      content.brands.forEach((b: any) => {
        brandNames.push(b?.name || b);
      });
    });

    const uniqueNames = [...new Set(brandNames)];
    uniqueNames.forEach(name => {
      const match = sourceBrands.find(b => b.name === name);
      if (match) {
        match.isSelected = true;
        match.isAutoSelected = true;
        contentBrands.push(match);
      }
    });

    return uniqBy(contentBrands, 'ID');
  }

  filteredBrands(): Brand[] {
    if (!this.brandService?.brands?.length) return [];
    return this.brandService.brands.filter(
      b => b.producttype !== 268890001
        && (!b.productApplicability.length || b.productApplicability.includes('100000000'))
    );
  }

}
